import { useEffect, useState, useCallback, useMemo } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Download,
  FileSpreadsheet,
  Calendar,
  Printer,
  Filter,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BarChart3,
} from "lucide-react";
import {
  server_post_data,
  get_department_with_categories,
  get_view_stock_inventory,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import * as XLSX from "xlsx";
import Loader from "../components/layout/Loader";

export default function StockInventoryReports() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [units, setUnits] = useState([]);

  // Filter States
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [stockStatus, setStockStatus] = useState("all");

  // Restaurant Info (you can fetch this from your API or localStorage)
  const restaurantName = retrieveData("restaurant_name") || "UpRestro";
  const restaurantAddress = retrieveData("restaurant_address") || "";

  // Fetch Departments
  const fetchDepartments = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const response = await server_post_data(
        get_department_with_categories,
        fd
      );
      if (!response.data.error && response.data.message?.data) {
        setDepartments(response.data.message.data);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, []);

  // Fetch Report Data
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append(
      "selected_id",
      selectedDepartment === "all" ? "0" : selectedDepartment
    );
    fd.append("flag", "0");

    try {
      const response = await server_post_data(get_view_stock_inventory, fd);
      if (!response.data.error && response.data.message?.ingredients_data) {
        setReportData(response.data.message.ingredients_data);
        if (selectedDepartment === "all") {
          setUnits(response.data.message.units_data || []);
        }
      } else {
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    if (departments.length > 0) {
      fetchReportData();
    }
  }, [departments, selectedDepartment, fetchReportData]);

  // Date Filter Handler
  const handleQuickDateFilter = (filter) => {
    setDateFilter(filter);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case "today":
        setFromDate(today.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFromDate(yesterday.toISOString().split("T")[0]);
        setToDate(yesterday.toISOString().split("T")[0]);
        break;
      case "week":
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        setFromDate(weekAgo.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        break;
      case "month":
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        setFromDate(monthAgo.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        break;
      case "all":
        setFromDate("");
        setToDate("");
        break;
      default:
        break;
    }
  };

  // Filtered Report Data
  const filteredReportData = useMemo(() => {
    let filtered = [...reportData];

    // Stock status filter
    if (stockStatus === "in-stock") {
      filtered = filtered.filter(
        (item) => item.balance_stock > item.minimum_stock_alert
      );
    } else if (stockStatus === "low-stock") {
      filtered = filtered.filter(
        (item) =>
          item.balance_stock > 0 &&
          item.balance_stock <= item.minimum_stock_alert
      );
    } else if (stockStatus === "out-of-stock") {
      filtered = filtered.filter((item) => item.balance_stock === 0);
    }

    return filtered;
  }, [reportData, stockStatus]);

  // Report Statistics
  const reportStats = useMemo(() => {
    const total = filteredReportData.length;
    const inStock = filteredReportData.filter(
      (i) => i.balance_stock > i.minimum_stock_alert
    ).length;
    const lowStock = filteredReportData.filter(
      (i) => i.balance_stock > 0 && i.balance_stock <= i.minimum_stock_alert
    ).length;
    const outOfStock = filteredReportData.filter(
      (i) => i.balance_stock === 0
    ).length;

    const totalValue = filteredReportData.reduce(
      (sum, i) => sum + i.balance_stock * (i.last_price || 0),
      0
    );

    const avgStockLevel =
      total > 0
        ? filteredReportData.reduce((sum, i) => sum + i.balance_stock, 0) /
          total
        : 0;

    return {
      total,
      inStock,
      lowStock,
      outOfStock,
      totalValue,
      avgStockLevel,
    };
  }, [filteredReportData]);

  const getUnitName = (defaultUnitId) => {
    const unit = units.find(
      (u) => String(u.primary_id) === String(defaultUnitId)
    );
    return unit ? unit.short_code_name || unit.unit_name : "—";
  };

  // Export to Excel with Professional Formatting
  const exportToExcel = () => {
    const departmentName =
      selectedDepartment === "all"
        ? "All Departments"
        : departments.find((d) => d.department_id === selectedDepartment)
            ?.department_name || "Unknown";

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([[]]);

    let currentRow = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["STOCK INVENTORY REPORT"]], {
      origin: { r: currentRow, c: 0 },
    });
    ws["!merges"] = [
      { s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 10 } },
    ];
    currentRow += 1;

    // Restaurant Name
    XLSX.utils.sheet_add_aoa(ws, [[restaurantName]], {
      origin: { r: currentRow, c: 0 },
    });
    ws["!merges"].push({
      s: { r: currentRow, c: 0 },
      e: { r: currentRow, c: 10 },
    });
    currentRow += 1;

    // Restaurant Address (if available)
    if (restaurantAddress) {
      XLSX.utils.sheet_add_aoa(ws, [[restaurantAddress]], {
        origin: { r: currentRow, c: 0 },
      });
      ws["!merges"].push({
        s: { r: currentRow, c: 0 },
        e: { r: currentRow, c: 10 },
      });
      currentRow += 1;
    }

    // Empty row
    currentRow += 1;

    // Report metadata
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [`Department: ${departmentName}`],
        [
          `Report Date: ${new Date().toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}`,
        ],
        [
          dateFilter !== "all" && fromDate && toDate
            ? `Period: ${new Date(fromDate).toLocaleDateString(
                "en-IN"
              )} to ${new Date(toDate).toLocaleDateString("en-IN")}`
            : "Period: All Time",
        ],
      ],
      { origin: { r: currentRow, c: 0 } }
    );
    currentRow += 4;

    // Summary Section
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], {
      origin: { r: currentRow, c: 0 },
    });
    ws["!merges"].push({
      s: { r: currentRow, c: 0 },
      e: { r: currentRow, c: 1 },
    });
    currentRow += 1;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [`Total Items: ${reportStats.total}`],
        [`In Stock: ${reportStats.inStock}`],
        [`Low Stock: ${reportStats.lowStock}`],
        [`Out of Stock: ${reportStats.outOfStock}`],
        [
          `Total Stock Value: ₹${reportStats.totalValue.toLocaleString(
            "en-IN"
          )}`,
        ],
      ],
      { origin: { r: currentRow, c: 0 } }
    );
    currentRow += 6;

    // Table Headers
    const headers = [
      "S.No",
      "Item ID",
      "Item Name",
      "Category",
      "Department",
      "Unit",
      "Current Stock",
      "Min Stock",
      "Avg Rate (₹)",
      "Stock Value (₹)",
      "Status",
    ];

    XLSX.utils.sheet_add_aoa(ws, [headers], {
      origin: { r: currentRow, c: 0 },
    });

    const headerRow = currentRow;
    currentRow += 1;

    // Table Data
    const tableData = filteredReportData.map((item, index) => [
      index + 1,
      `LI${String(item.primary_id).padStart(4, "0")}`,
      item.ingredient_name,
      item.dcategory_name || "—",
      item.department_name || "—",
      getUnitName(item.default_unit_id),
      item.balance_stock,
      item.minimum_stock_alert,
      item.last_price || 0,
      (item.balance_stock * (item.last_price || 0)).toFixed(2),
      item.balance_stock === 0
        ? "Out of Stock"
        : item.balance_stock <= item.minimum_stock_alert
        ? "Low Stock"
        : "In Stock",
    ]);

    XLSX.utils.sheet_add_aoa(ws, tableData, {
      origin: { r: currentRow, c: 0 },
    });
    currentRow += tableData.length;

    // Footer
    currentRow += 1;
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          `Report Generated: ${new Date().toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
          })}`,
        ],
      ],
      { origin: { r: currentRow, c: 0 } }
    );
    ws["!merges"].push({
      s: { r: currentRow, c: 0 },
      e: { r: currentRow, c: 5 },
    });

    // Set Column Widths
    ws["!cols"] = [
      { wch: 6 }, // S.No
      { wch: 12 }, // Item ID
      { wch: 30 }, // Item Name
      { wch: 20 }, // Category
      { wch: 15 }, // Department
      { wch: 10 }, // Unit
      { wch: 15 }, // Current Stock
      { wch: 12 }, // Min Stock
      { wch: 15 }, // Avg Rate
      { wch: 18 }, // Stock Value
      { wch: 15 }, // Status
    ];

    // Apply Styles
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Style Title (Row 0)
    const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
    if (!ws[titleCell]) ws[titleCell] = { t: "s", v: "" };
    ws[titleCell].s = {
      font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "0066CC" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // Style Restaurant Name (Row 1)
    const nameCell = XLSX.utils.encode_cell({ r: 1, c: 0 });
    if (!ws[nameCell]) ws[nameCell] = { t: "s", v: "" };
    ws[nameCell].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // Style Restaurant Address (Row 2)
    if (restaurantAddress) {
      const addressCell = XLSX.utils.encode_cell({ r: 2, c: 0 });
      if (!ws[addressCell]) ws[addressCell] = { t: "s", v: "" };
      ws[addressCell].s = {
        font: { sz: 10, italic: true },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    // Style Summary Header
    const summaryHeaderRow = restaurantAddress ? 8 : 7;
    const summaryHeaderCell = XLSX.utils.encode_cell({
      r: summaryHeaderRow,
      c: 0,
    });
    if (!ws[summaryHeaderCell]) ws[summaryHeaderCell] = { t: "s", v: "" };
    ws[summaryHeaderCell].s = {
      font: { bold: true, sz: 12 },
      fill: { fgColor: { rgb: "E0E0E0" } },
      alignment: { horizontal: "left", vertical: "center" },
    };

    // Style Table Headers
    for (let col = 0; col <= 10; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: col });
      if (!ws[cellAddress]) ws[cellAddress] = { t: "s", v: "" };
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
    }

    // Style Table Data Rows with alternating colors and borders
    for (
      let row = headerRow + 1;
      row < headerRow + 1 + tableData.length;
      row++
    ) {
      const isEvenRow = (row - headerRow) % 2 === 0;
      const item = filteredReportData[row - headerRow - 1];

      for (let col = 0; col <= 10; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;

        // Determine row background color based on stock status
        let bgColor = isEvenRow ? "FFFFFF" : "F2F2F2";
        if (item.balance_stock === 0) {
          bgColor = "FFE6E6"; // Light red for out of stock
        } else if (item.balance_stock <= item.minimum_stock_alert) {
          bgColor = "FFF4E6"; // Light amber for low stock
        }

        ws[cellAddress].s = {
          fill: { fgColor: { rgb: bgColor } },
          alignment: {
            horizontal: col === 0 || col >= 6 ? "center" : "left",
            vertical: "center",
          },
          border: {
            top: { style: "thin", color: { rgb: "D0D0D0" } },
            bottom: { style: "thin", color: { rgb: "D0D0D0" } },
            left: { style: "thin", color: { rgb: "D0D0D0" } },
            right: { style: "thin", color: { rgb: "D0D0D0" } },
          },
        };

        // Bold font for numeric values
        if (col === 6 || col === 9) {
          ws[cellAddress].s.font = { bold: true };
        }

        // Format currency cells
        if (col === 8 || col === 9) {
          ws[cellAddress].z = "₹#,##0.00";
        }
      }
    }

    // Add row height
    ws["!rows"] = [];
    ws["!rows"][0] = { hpt: 25 }; // Title row height
    ws["!rows"][headerRow] = { hpt: 20 }; // Header row height

    // Append worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Stock Report");

    // Generate filename
    const date = new Date().toISOString().split("T")[0];
    const filename = `Stock_Inventory_Report_${departmentName.replace(
      /\s+/g,
      "_"
    )}_${date}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);
  };

  // Print Report
  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <Header
        title="Stock Inventory Reports"
        subtitle="Generate and export detailed stock inventory reports"
      />

      {loading && <Loader />}

      <div className="p-6 space-y-6">
        {/* Filters Card */}
        <Card className="border-primary/20 print:hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Report Filters
                </CardTitle>
                <CardDescription>
                  Configure your report parameters
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handlePrint}
                  variant="outline"
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </Button>
                <Button onClick={exportToExcel} className="gap-2">
                  <Download className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Department and Status Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={selectedDepartment}
                  onValueChange={setSelectedDepartment}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.department_id}
                        value={dept.department_id}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Stock Status</Label>
                <Select value={stockStatus} onValueChange={setStockStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="in-stock">In Stock</SelectItem>
                    <SelectItem value="low-stock">Low Stock</SelectItem>
                    <SelectItem value="out-of-stock">Out of Stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date Filters */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Report Period
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={dateFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("all")}
                  >
                    All Time
                  </Button>
                  <Button
                    variant={dateFilter === "today" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("today")}
                  >
                    Today
                  </Button>
                  <Button
                    variant={dateFilter === "yesterday" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("yesterday")}
                  >
                    Yesterday
                  </Button>
                  <Button
                    variant={dateFilter === "week" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("week")}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant={dateFilter === "month" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("month")}
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    variant={dateFilter === "custom" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDateFilter("custom")}
                  >
                    Custom Range
                  </Button>
                </div>
              </div>

              {/* Custom Date Range */}
              {dateFilter === "custom" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg border">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      min={fromDate}
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={fetchReportData}
              className="w-full gap-2"
              size="lg"
            >
              <BarChart3 className="w-4 h-4" />
              Generate Report
            </Button>
          </CardContent>
        </Card>

        {/* Report Content */}
        <div className="space-y-6 print:space-y-4">
          {/* Report Header */}
          <Card className="border-2 print:border-0 print:shadow-none">
            <CardContent className="pt-6">
              <div className="text-center space-y-2 border-b pb-6 mb-6">
                <h1 className="text-3xl font-bold text-primary print:text-black">
                  {restaurantName}
                </h1>
                {restaurantAddress && (
                  <p className="text-sm text-muted-foreground print:text-gray-600">
                    {restaurantAddress}
                  </p>
                )}
                <h2 className="text-xl font-semibold mt-4">
                  STOCK INVENTORY REPORT
                </h2>
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground print:text-gray-600">
                  <span>
                    Department:{" "}
                    <strong>
                      {selectedDepartment === "all"
                        ? "All Departments"
                        : departments.find(
                            (d) => d.department_id === selectedDepartment
                          )?.department_name || "Unknown"}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>
                    Report Date:{" "}
                    <strong>
                      {new Date().toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </strong>
                  </span>
                  {dateFilter !== "all" && fromDate && toDate && (
                    <>
                      <span>•</span>
                      <span>
                        Period: <strong>{fromDate}</strong> to{" "}
                        <strong>{toDate}</strong>
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="bg-primary/5 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-muted-foreground print:text-gray-600">
                    Total Items
                  </p>
                  <p className="text-2xl font-bold text-primary print:text-black">
                    {reportStats.total}
                  </p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-emerald-700 print:text-gray-600">
                    In Stock
                  </p>
                  <p className="text-2xl font-bold text-emerald-600 print:text-black">
                    {reportStats.inStock}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-amber-700 print:text-gray-600">
                    Low Stock
                  </p>
                  <p className="text-2xl font-bold text-amber-600 print:text-black">
                    {reportStats.lowStock}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-red-700 print:text-gray-600">
                    Out of Stock
                  </p>
                  <p className="text-2xl font-bold text-red-600 print:text-black">
                    {reportStats.outOfStock}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-blue-700 print:text-gray-600">
                    Avg Stock
                  </p>
                  <p className="text-2xl font-bold text-blue-600 print:text-black">
                    {reportStats.avgStockLevel.toFixed(0)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-purple-700 print:text-gray-600">
                    Total Value
                  </p>
                  <p className="text-2xl font-bold text-purple-600 print:text-black">
                    ₹{(reportStats.totalValue / 1000).toFixed(1)}K
                  </p>
                </div>
              </div>

              {/* Stock Status Alert */}
              {reportStats.lowStock + reportStats.outOfStock > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 print:bg-gray-100 print:border-gray-300">
                  <AlertTriangle className="w-5 h-5 text-amber-600 print:text-black" />
                  <div>
                    <p className="font-medium text-amber-800 print:text-black">
                      Attention Required
                    </p>
                    <p className="text-sm text-amber-700 print:text-gray-600">
                      {reportStats.lowStock + reportStats.outOfStock} items need
                      restocking ({reportStats.lowStock} low stock,{" "}
                      {reportStats.outOfStock} out of stock)
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detailed Report Table */}
          <Card className="print:border-0 print:shadow-none">
            <CardHeader className="print:pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Detailed Stock Report
              </CardTitle>
              <CardDescription className="print:text-gray-600">
                Showing {filteredReportData.length} items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-auto print:border-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 print:bg-gray-100">
                      <TableHead className="w-16 print:text-black">
                        S.No
                      </TableHead>
                      <TableHead className="print:text-black">
                        Item ID
                      </TableHead>
                      <TableHead className="print:text-black">
                        Item Name
                      </TableHead>
                      <TableHead className="print:text-black">
                        Category
                      </TableHead>
                      <TableHead className="print:text-black">
                        Department
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Unit
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Current Stock
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Min Stock
                      </TableHead>
                      <TableHead className="text-right print:text-black">
                        Avg Rate
                      </TableHead>
                      <TableHead className="text-right print:text-black">
                        Stock Value
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReportData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          className="text-center py-12 text-muted-foreground"
                        >
                          No data available for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredReportData.map((item, index) => (
                        <TableRow
                          key={item.primary_id}
                          className={
                            item.balance_stock === 0
                              ? "bg-red-50/50 print:bg-gray-100"
                              : item.balance_stock <= item.minimum_stock_alert
                              ? "bg-amber-50/50 print:bg-gray-50"
                              : ""
                          }
                        >
                          <TableCell className="font-medium print:text-black">
                            {index + 1}
                          </TableCell>
                          <TableCell className="font-mono text-xs print:text-black">
                            LI{String(item.primary_id).padStart(4, "0")}
                          </TableCell>
                          <TableCell className="font-medium print:text-black">
                            {item.ingredient_name}
                          </TableCell>
                          <TableCell className="print:text-black">
                            {item.dcategory_name || "—"}
                          </TableCell>
                          <TableCell className="print:text-black">
                            {item.department_name || "—"}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {getUnitName(item.default_unit_id)}
                          </TableCell>
                          <TableCell className="text-center font-semibold print:text-black">
                            {item.balance_stock}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {item.minimum_stock_alert}
                          </TableCell>
                          <TableCell className="text-right print:text-black">
                            ₹{item.last_price || 0}
                          </TableCell>
                          <TableCell className="text-right font-medium print:text-black">
                            ₹
                            {(
                              item.balance_stock * (item.last_price || 0)
                            ).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              className={
                                item.balance_stock === 0
                                  ? "bg-red-100 text-red-700 print:bg-white print:text-black print:border print:border-black"
                                  : item.balance_stock <=
                                    item.minimum_stock_alert
                                  ? "bg-amber-100 text-amber-700 print:bg-white print:text-black print:border print:border-black"
                                  : "bg-emerald-100 text-emerald-700 print:bg-white print:text-black print:border print:border-black"
                              }
                            >
                              {item.balance_stock === 0
                                ? "Out"
                                : item.balance_stock <= item.minimum_stock_alert
                                ? "Low"
                                : "OK"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Report Footer */}
              <div className="mt-6 pt-6 border-t space-y-2 text-sm text-muted-foreground print:text-gray-600">
                <div className="flex justify-between">
                  <span>Report Generated On:</span>
                  <span className="font-medium">
                    {new Date().toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Records:</span>
                  <span className="font-medium">
                    {filteredReportData.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Stock Value:</span>
                  <span className="font-medium text-lg">
                    ₹{reportStats.totalValue.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:block,
          .print\\:block * {
            visibility: visible;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </MainLayout>
  );
}
